// @flow strict

import { Container } from '@material-ui/core';
import React, { useState } from 'react';

import { Link, graphql, useStaticQuery } from 'gatsby';

import { useLanguageContext } from '~contexts/language-context';

import { MagazineIcon } from '~components/icons/magazine-icon';
import {
  MobileMenuOpenIcon,
  MobileMenuCloseIcon,
} from '~components/icons/mobile-menu-icon';
import { MobileMenuItem } from './mobileMenuItem';
import { LanguageSwitch } from '~components/language-switch/language-switch';
import { MobileLanguageSwitch } from '~components/language-switch/mobile-language-switch';
import { Modal } from '~components/modal/modal';
import { SearchIcon } from '~components/icons/search-icon';

import { createLinkPath } from '~utils/helpers/create-link-path';

import type { Alternates } from '~types/post';

import * as styles from './header.module.less';

/**
 * Type of component properties.
 */
type HeaderProps = {
  alternates: Alternates,
  showWebsiteNavigationItemsOnMobileOnly: Boolean,
  showBlogLink?: Boolean,
};

/**
 * Head of the page.
 *
 * @param {HeaderProps} props - component properties
 *
 * @returns {Node}
 */
export const Header = ({
  alternates,
  showWebsiteNavigationItemsOnMobileOnly = false,
  showBlogLink = false,
}: HeaderProps) => {
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);

  const { language, loc } = useLanguageContext();

  // get all categories
  const data = useStaticQuery(graphql`
    query CategoriesQuery {
      allCategoriesJson {
        nodes {
          name
          language
          link
        }
      }
      allNavigationJson {
        nodes {
          name
          language
          items {
            name
            href
            image {
              src {
                publicURL
              }
              alt
            }
          }
          href
        }
      }
    }
  `);

  // get all categories from query result
  // and filter for language
  const categories = data.allCategoriesJson.nodes.filter(
    (category) => category.language === language,
  );

  // get all navigation items from query result
  // and filter for language
  const navigationItems = data.allNavigationJson.nodes.filter(
    (navigationItem) => navigationItem.language === language,
  );

  // click handler to open mobile menu
  const openMobileMenu = () => {
    setMobileMenuIsOpen(true);
  };

  return (
    <header className={styles.header}>
      {/* Logo */}
      {!showWebsiteNavigationItemsOnMobileOnly && (
        <figure className={styles.logo}>
          <a href={createLinkPath({ path: '/blog', language })}>
            <MagazineIcon />
          </a>
        </figure>
      )}

      {/* Main navigation */}
      <nav className={styles.navigation}>
        {categories.map((category) => {
          return (
            <Link
              to={createLinkPath({ path: `/blog${category.link}`, language })}
              activeClassName={styles.active}
              partiallyActive={true}
              key={category.link}
            >
              {category.name}
            </Link>
          );
        })}
      </nav>
      {/* Language switch */}
      <div className={styles.languageSwitch}>
        <LanguageSwitch alternates={alternates} />
      </div>

      {/* Search icon */}
      <Link
        className={styles.search}
        to={createLinkPath({ path: `/blog/search`, language })}
      >
        <SearchIcon />
      </Link>

      {/* Mobile menu icon */}
      <MobileMenuOpenIcon
        className={styles.mobileMenuIcon}
        onClick={openMobileMenu}
      />

      {/* Mobile menu */}
      {mobileMenuIsOpen && (
        <Modal
          className={styles.mobileMenu}
          onClose={() => setMobileMenuIsOpen(false)}
        >
          {/* Head/Logo of mobile menu */}
          <Container fixed maxWidth={false}>
            <div className={styles.mobileMenu__header}>
              {!showWebsiteNavigationItemsOnMobileOnly && (
                <MagazineIcon className={styles.logo} />
              )}
              <MobileMenuCloseIcon
                className={styles.closeIcon}
                onClick={() => setMobileMenuIsOpen(false)}
              />
            </div>
          </Container>

          {/* Mobile menu content / main navigation */}
          <div className={styles.mobileMenu__content}>
            <Container fixed maxWidth={false}>
              <div className={styles.mobileMenu__contentWrapper}>
                <MobileLanguageSwitch alternates={alternates} />
                <nav>
                  {!showWebsiteNavigationItemsOnMobileOnly &&
                    categories.map((category) => {
                      return (
                        <Link
                          to={createLinkPath({
                            path: `/blog${category.link}`,
                            language,
                          })}
                          activeClassName={styles.active}
                          partiallyActive={true}
                          key={category.link}
                        >
                          {category.name}
                        </Link>
                      );
                    })}
                  {navigationItems.map((navigationItem) => {
                    return (
                      <MobileMenuItem
                        key={navigationItem.name}
                        data={navigationItem}
                      />
                    );
                  })}
                  {showBlogLink && (
                    <Link to={createLinkPath({ path: '/blog', language })}>
                      Blog
                    </Link>
                  )}
                  {!showWebsiteNavigationItemsOnMobileOnly && (
                    <Link
                      to={createLinkPath({ path: `/blog/search`, language })}
                    >
                      <SearchIcon /> {loc('_search_menu_item')}
                    </Link>
                  )}
                </nav>
              </div>
            </Container>
          </div>
        </Modal>
      )}
    </header>
  );
};
